import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Award, GraduationCap, FlaskConical, Crown, X, Calendar, Building2 } from "lucide-react";
import { AWARDS } from "./data";
import { useContent } from "./useContent";
import { resolveFileUrl } from "./fileUrl";

const CATEGORY_META = {
  Sport: { Icon: Trophy, color: "#e6c769" },
  Academic: { Icon: GraduationCap, color: "#a5b4fc" },
  Research: { Icon: FlaskConical, color: "#c4b5fd" },
  Leadership: { Icon: Crown, color: "#86efac" },
};

const AwardCard = ({ award, onClick, index }) => {
  const meta = CATEGORY_META[award.category] || { Icon: Award, color: "#fff" };
  const { Icon, color } = meta;

  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.07, ease: [0.16, 1, 0.3, 1] }}
      className="group relative text-left rounded-[22px] overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.05] to-white/[0.01] backdrop-blur-xl hover:border-white/25 transition-all hover:-translate-y-1"
    >
      {/* Floating accent glow */}
      <div
        className="absolute -top-16 -right-16 w-44 h-44 rounded-full opacity-40 group-hover:opacity-70 transition-opacity blur-3xl"
        style={{ background: `${color}33` }}
      />

      {/* Image (if present) */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {award.image ? (
          <>
            <img
              src={resolveFileUrl(award.image)}
              alt={award.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.06]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />
          </>
        ) : (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 30% 35%, ${color}26, transparent 55%), radial-gradient(circle at 80% 80%, ${color}1a, transparent 50%)`,
            }}
          />
        )}

        {/* If no image: large icon */}
        {!award.image && (
          <div className="absolute inset-0 grid place-items-center">
            <div className="relative">
              <div
                className="w-28 h-28 rounded-full grid place-items-center backdrop-blur-xl border"
                style={{ background: `${color}10`, borderColor: `${color}40` }}
              >
                <Icon className="w-12 h-12" style={{ color }} />
              </div>
              <div
                className="absolute inset-0 rounded-full blur-2xl -z-10"
                style={{ background: `${color}30` }}
              />
            </div>
          </div>
        )}

        {/* Category chip */}
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.25em] uppercase font-mono backdrop-blur-md border"
            style={{ borderColor: `${color}55`, color, background: `${color}10` }}
          >
            <Icon className="w-3 h-3" />
            {award.category}
          </span>
        </div>

        {/* Year stamp */}
        <div className="absolute top-4 right-4 text-[11px] tracking-[0.3em] uppercase text-white/60 font-mono">
          {award.year}
        </div>
      </div>

      {/* Body */}
      <div className="relative p-6">
        <div className="text-[11px] tracking-[0.3em] uppercase text-white/40 font-mono">
          {award.subtitle}
        </div>
        <h3
          className="mt-2 text-[22px] md:text-[24px] leading-tight text-white"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {award.title}
        </h3>
        <div className="mt-3 text-[12px] text-white/55 flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5" />
          <span className="truncate">{award.org}</span>
        </div>

        {/* Hover line */}
        <div
          className="absolute left-0 right-0 bottom-0 h-px opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
        />
      </div>
    </motion.button>
  );
};

const Awards = () => {
  const [open, setOpen] = React.useState(null);
  const items = useContent("awards", AWARDS);

  return (
    <section id="awards" className="relative py-28 px-6 md:px-10">
      <div className="max-w-[1300px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">
            Distinctions · Trophy cabinet
          </div>
          <div className="mt-3 grid lg:grid-cols-2 gap-10 items-end">
            <h2
              className="text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Awards &amp; <span className="italic">trophies</span>.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-md justify-self-end">
              A small gallery of moments that meant something. Sport, scholarship, research, leadership — held in floating glass and warm light. Click any card to see the story.
            </p>
          </div>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((a, i) => (
            <AwardCard key={a.id} award={a} onClick={() => setOpen(a)} index={i} />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-2xl flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 20 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-[1000px] w-full max-h-[88vh] overflow-auto rounded-[24px] border border-white/10 bg-gradient-to-br from-[#0c0d12] to-[#06070b]"
            >
              <div
                className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-50"
                style={{ background: `${open.accent}30` }}
              />
              <button
                onClick={() => setOpen(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 grid place-items-center text-white"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative grid md:grid-cols-2 gap-0">
                {/* Visual */}
                <div className="relative aspect-[4/5] md:aspect-auto md:min-h-[440px] bg-black">
                  {open.image ? (
                    <img src={resolveFileUrl(open.image)} alt={open.title} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center" style={{
                      backgroundImage: `radial-gradient(circle at 50% 40%, ${open.accent}30, transparent 60%)`,
                    }}>
                      {(() => {
                        const M = CATEGORY_META[open.category] || { Icon: Award };
                        const Icon = M.Icon;
                        return (
                          <div className="relative">
                            <div
                              className="w-44 h-44 rounded-full grid place-items-center backdrop-blur-xl border"
                              style={{ background: `${open.accent}14`, borderColor: `${open.accent}55` }}
                            >
                              <Icon className="w-20 h-20" style={{ color: open.accent }} />
                            </div>
                            <div className="absolute inset-0 rounded-full blur-3xl -z-10" style={{ background: `${open.accent}40` }} />
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Detail */}
                <div className="p-8 md:p-10 relative">
                  <div
                    className="text-[11px] tracking-[0.4em] uppercase font-mono"
                    style={{ color: open.accent }}
                  >
                    {open.category} · {open.year}
                  </div>
                  <h3
                    className="mt-3 text-[28px] md:text-[40px] leading-[1.05] tracking-tight text-white"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {open.title}
                  </h3>
                  <div className="mt-2 text-white/70 text-[15px]">{open.subtitle}</div>

                  <div className="mt-6 space-y-3 text-[13px] text-white/65">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-white/40" />
                      {open.org}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-white/40" />
                      {open.date}
                    </div>
                  </div>

                  <div className="mt-6 h-px bg-white/10" />

                  <p className="mt-6 text-[15px] leading-[1.75] text-white/70">
                    {open.detail}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Awards;
